import { EspeceCreateInput, EspeceListResponse, EspeceResponse, EspeceSelectInput, EspeceSelectOption, EspeceUpdateInput } from "@repo/types";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../lib/api/services';
import { useQueryWithConfig } from '../useQueryWithConfig';

// Hooks for fetching especes
export const useEspeces = (
  filters: EspeceSelectInput,
  queryOptions?: { enabled?: boolean; staleTime?: number }
) => {
  const { page = 1, limit = 10, ...filterParams } = filters;

  const searchParams = Object.fromEntries(
    Object.entries(filterParams).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
  );

  const keys = [
    "especes",
    "list",
    page,
    limit,
    Object.keys(searchParams).length > 0 ? JSON.stringify(searchParams) : null,
  ].filter(Boolean);

  return useQueryWithConfig<EspeceListResponse>(
    keys as string[],
    () =>
      services.espece.getEspeces({
        page,
        limit,
        includeDeleted: false,
        ...searchParams,
      }),
    {
      staleTime: 1000 * 60,
      ...queryOptions,
    }
  );
};

export const useEspece = (id: number) => {
  return useQueryWithConfig<EspeceResponse>(
    ["especes", "detail", id],
    () => services.espece.getEspeceById(id),
    {
      enabled: !!id,
    }
  );
};

export const useEspeceOptions = () => {
  return useQueryWithConfig<EspeceSelectOption[]>(
    ["especes", "options"],
    () => services.espece.getEspeceOptions()
  );
};

// Hooks for espece mutations
export const useCreateEspece = () => {
  const queryClient = useQueryClient();
  
  return useMutation<EspeceResponse, Error, EspeceCreateInput>({
    mutationFn: (data) => services.espece.createEspece(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["especes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["especes", "options"] });
    },
  });
};

export const useUpdateEspece = () => {
  const queryClient = useQueryClient();
  
  return useMutation<EspeceResponse, Error, { id: number; data: EspeceUpdateInput }>({
    mutationFn: ({ id, data }) => services.espece.updateEspece(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["especes", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["especes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["especes", "options"] });
    },
  });
};

export const useDeleteEspece = () => {
  const queryClient = useQueryClient();
  
  return useMutation<EspeceResponse, Error, number>({
    mutationFn: (id) => services.espece.deleteEspece(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["especes", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["especes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["especes", "options"] });
    },
  });
};

export const useRestoreEspece = () => {
  const queryClient = useQueryClient();
  
  return useMutation<EspeceResponse, Error, number>({
    mutationFn: (id) => services.espece.restoreEspece(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["especes", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["especes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["especes", "options"] });
    },
  });
};

export const useUploadEspeceImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation<EspeceResponse, Error, { id: number; imageFile: File }>({
    mutationFn: ({ id, imageFile }) => services.espece.uploadEspeceImage(id, imageFile),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["especes", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["especes", "list"] });
    },
  });
};
